import { type ReactNode, useLayoutEffect, useState } from 'react';
import styles from './SlidingIndicator.module.css';

const ACTIVE_OPTION = '[aria-pressed="true"], [aria-current="page"], [aria-checked="true"]';

export function placeIndicator(indicator: HTMLElement, group: HTMLElement): void {
  const active = group.querySelector<HTMLElement>(ACTIVE_OPTION);
  if (active === null) {
    indicator.style.opacity = '0';
    return;
  }
  // Rects keep the fractional widths of flex: 1 options that offsetLeft/offsetWidth round away.
  const option = active.getBoundingClientRect();
  const paddingEdge = group.getBoundingClientRect().left + parseFloat(getComputedStyle(group).borderLeftWidth);
  indicator.style.opacity = '1';
  indicator.style.setProperty('--indicator-left', `${option.left - paddingEdge}px`);
  indicator.style.setProperty('--indicator-right', `${option.right - paddingEdge}px`);
}

/** Places the indicator, turning its slide on only once the group has been placed while visible. */
function syncIndicator(indicator: HTMLElement, group: HTMLElement): void {
  // A hidden group (display: none) measures 0 wide; sliding from that placement would grow the indicator from x = 0.
  if (group.getBoundingClientRect().width === 0) {
    delete indicator.dataset.ready;
    return;
  }
  placeIndicator(indicator, group);
  if (indicator.dataset.ready === 'true') return;
  // Flush this placement before transitions turn on, so the indicator appears in place instead of sliding in.
  indicator.getBoundingClientRect();
  indicator.dataset.ready = 'true';
}

export interface SlidingIndicatorProps {
  variant: 'fill' | 'underline';
  /** The options' labels in order, copied in the active colours where the indicator covers them. */
  labels: readonly ReactNode[];
  /** The options' class, so each copy lays out exactly over its option. */
  labelClassName: string | undefined;
}

export function SlidingIndicator({ variant, labels, labelClassName }: SlidingIndicatorProps) {
  const [node, setNode] = useState<HTMLSpanElement | null>(null);
  useLayoutEffect(() => {
    if (node === null) return undefined;
    const group = node.parentElement as HTMLElement;
    const place = (): void => syncIndicator(node, group);
    place();
    const mutations = new MutationObserver(place);
    mutations.observe(group, { subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'aria-current', 'aria-checked'] });
    const resize = new ResizeObserver(place);
    resize.observe(group);
    return () => {
      mutations.disconnect();
      resize.disconnect();
    };
  }, [node]);
  return (
    <span ref={setNode} className={styles.indicator} data-variant={variant} aria-hidden="true">
      <span className={styles.clipLeft}>
        <span className={styles.unshift}>
          <span className={styles.clipRight}>
            <span className={styles.labels}>
              {labels.map((label, i) => <span key={i} className={labelClassName}>{label}</span>)}
            </span>
          </span>
        </span>
      </span>
    </span>
  );
}
